import { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { QueryClient } from '@tanstack/react-query';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

// utils & misc
import renderBlock from '../../src/utils/blocks';
import getServerSideTranslations from '../../src/utils/server-side';
import { pageSlugToTitle } from '../../src/utils/pageSlug';
import orderResidenceByOpeningDate from '../../src/utils/orderResidenceByOpeningDate';
import {
  MEDIUM_LONG_STAY, URL_SEARCH_PAGES,
} from '../../src/constants';

// Services
import { getContentTranslationProxy } from '../../src/services/content-translations';

// Components
import PageTransition from '../../components/templates/PageTransition/PageTransition';
import PageLayout from '../../components/templates/PageLayout/PageLayout';
import Search from '../../components/templates/Search/Search';
import BreadCrumb from '../../components/atoms/BreadCrumb/BreadCrumb';

// Hooks
import useSeoLinks from '../../src/hooks/useSeoLinks';

function Index({
  page,
  pagesResidences,
}) {
  // Hooks
  const { t } = useTranslation();
  const router = useRouter();
  const { canonicalUrl, alternateLinks } = useSeoLinks();
  // States
  const breadcrumbItems = [
    {
      name: t('breadcrumb.home'),
      position: 1,
      item: '/',
    },
    {
      name: pageSlugToTitle(URL_SEARCH_PAGES[router.locale]),
      position: 2,
      item: `/${URL_SEARCH_PAGES[router.locale]}`,
    },
  ];

  // Data
  const pageData = useMemo(() => page?.data || {}, [page]);
  const residences = useMemo(() => {
    if (pagesResidences?.length > 0) {
      return orderResidenceByOpeningDate(pagesResidences);
    }
    return [];
  }, [pagesResidences]);

  return (
    <>
      <Head>
        <title>{pageData?.metaTitle}</title>
        <meta name="description" content={pageData?.metaDescription} />

        <link rel="canonical" href={canonicalUrl} />
        {alternateLinks.map(({ href, hrefLang }) => (
          <link key={hrefLang} rel="alternate" href={href} hrefLang={hrefLang} />
        ))}
      </Head>

      <PageLayout
        header={pageData?.header?.data}
        footer={pageData?.footer?.data}
      >
        <PageTransition>
          <Search list={residences} pageData={pageData}>
            {pageData?.blocks?.map((block, index) => (
              <Fragment key={`${block.type}-${index}`}>
                {renderBlock(block)}
              </Fragment>
            ))}
            <BreadCrumb items={breadcrumbItems} theme="transparent" />
          </Search>
        </PageTransition>
      </PageLayout>
    </>
  );
}

Index.propTypes = {
  page: PropTypes.shape({
    data: PropTypes.shape({
      blocks: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.string,
        data: PropTypes.shape({}),
      })),
    }),
  }).isRequired,
  pagesResidences: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({
        residence: PropTypes.shape({
          data: PropTypes.shape({}),
        }),
        slug: PropTypes.string,
      }),
    }),
  ).isRequired,
};

export async function getServerSideProps({ locale, query }) {
  const translations = await getServerSideTranslations(locale);
  const preview = { preview: query.previewMode ? true : null, contentId: query.contentId || null };
  const queryClient = new QueryClient();

  const pageContentTypeId = 'search';
  const defaultPageSlug = {
    fr: 'fr-default',
    en: 'en-default',
  };

  // API calls
  const page = await queryClient.fetchQuery(
    ['page', { slug: defaultPageSlug[locale], contentTypeId: pageContentTypeId, locale }],
    () => getContentTranslationProxy({
      contentTypeId: pageContentTypeId,
      slug: defaultPageSlug[locale],
      ...preview,
    }, locale, query.previewMode)
      .then(async (res) => {
        if (res?.data?.length) {
          return res?.data?.[0];
        }
        return {};
      }),
  );

  const residences = await queryClient.fetchQuery(
    ['page', { contentTypeId: 'residence', locale }],
    () => getContentTranslationProxy({
      pageSize: 99,
      contentTypeId: 'residence',
      depth: 1,
    }, locale)
      .then(async (res) => {
        if (res?.data?.length) {
          return res?.data?.reduce(
            (acc, residenceData) => ({ ...acc, [residenceData?.contentId]: residenceData }),
            {},
          );
        }

        return {};
      }),
  );

  const pagesResidences = await Promise.all(
    Object.keys(residences)?.map((residenceContentId) => queryClient.fetchQuery(
      ['page', { contentTypeId: 'page-residence', referencingContentId: residenceContentId || null, locale }],
      () => getContentTranslationProxy({
        contentTypeId: 'page-residence',
        referencingContentId: residenceContentId || null,
      }, locale)
        .then(async (res) => {
          if (res?.data?.length > 0) {
            const longStayFilteredPages = res?.data
              ?.filter((residenceData) => (
                residenceData?.data?.activity === MEDIUM_LONG_STAY
              ));
            return longStayFilteredPages;
          }
          return null;
        }),
    )),
  ).then((res) => res?.filter((residenceData) => residenceData?.length > 0).flat());

  return {
    props: {
      page,
      residences,
      pagesResidences,
      ...translations,
    },
  };
}

export default Index;
